import { z } from "zod";
import { organizationConnectionPaginationQuerySchema } from "./organization-connection-pagination.schema";

export const organizationIntegrationEntitySchema = z.object({
    id: z.number(),
    name: z.string().nullable(),
    isBusiness: z.boolean(),
    iconColor: z.string(),
    logo: z.string().nullable(),
});

export const organizationIntegrationWorkspaceSchema = z.object({
    id: z.string().uuid(),
    name: z.string().nullable(),
    entities: z.array(organizationIntegrationEntitySchema),
});

/**
 * Client + entity options for the assignment dropdowns: the clients the caller
 * can access in the org (same scope as the org clients list), each with its
 * entities.
 */
export const organizationIntegrationWorkspacesResponseSchema = z.object({
    workspaces: z.array(organizationIntegrationWorkspaceSchema),
});

export type OrganizationIntegrationWorkspacesResponse = z.infer<
    typeof organizationIntegrationWorkspacesResponseSchema
>;

/** The entity an account is assigned to — enough to render its icon + label. */
export const organizationIntegrationAccountEntitySchema = z.object({
    id: z.number(),
    name: z.string().nullable(),
    isBusiness: z.boolean(),
    iconColor: z.string(),
    logo: z.string().nullable(),
    workspaceId: z.string(),
    /** Name of the client (workspace) the entity belongs to. */
    workspaceName: z.string().nullable(),
});

export type OrganizationIntegrationAccountEntity = z.infer<
    typeof organizationIntegrationAccountEntitySchema
>;

/**
 * A Plaid account under a connection — the lean shape the table needs (not the
 * full FinancialAccountDto). Keeping it lean both enables runtime response
 * validation and avoids exposing unrelated account fields.
 */
export const organizationIntegrationAccountSchema = z.object({
    id: z.number(),
    entityId: z.number().nullable(),
    name: z.string().nullable(),
    accountNumberMask: z.string().nullable(),
    isBusiness: z.boolean().nullable(),
    entity: organizationIntegrationAccountEntitySchema.nullable(),
});

export type OrganizationIntegrationAccount = z.infer<
    typeof organizationIntegrationAccountSchema
>;

/**
 * A Plaid connection (one bank login) the caller created, with its accounts.
 * Accounts include unassigned ones (entity === null); they're assigned inline
 * and stay grouped under their connection.
 */
export const organizationIntegrationConnectionSchema = z.object({
    id: z.number(),
    bankName: z.string().nullable(),
    bankLogoUrl: z.string().nullable(),
    error: z.string().nullable(),
    accounts: z.array(organizationIntegrationAccountSchema),
});

export type OrganizationIntegrationConnection = z.infer<
    typeof organizationIntegrationConnectionSchema
>;

export const organizationIntegrationConnectionsQuerySchema =
    organizationConnectionPaginationQuerySchema;

export type OrganizationIntegrationConnectionsQuery = z.infer<
    typeof organizationIntegrationConnectionsQuerySchema
>;

export const organizationIntegrationConnectionsResponseSchema = z.object({
    connections: z.array(organizationIntegrationConnectionSchema),
    total: z.number(),
    page: z.number(),
    pageCount: z.number(),
});

export type OrganizationIntegrationConnectionsResponse = z.infer<
    typeof organizationIntegrationConnectionsResponseSchema
>;

/**
 * A single financial-account -> entity assignment as part of a batch. Used by
 * the multi-provider Add-account modal to persist per-account client/entity
 * picks after connecting an institution, and by the integrations table to move
 * an already-assigned account to another entity of the same client. Every
 * assignment is validated up front (accessible entity, accessible account, no
 * client change) before any are applied, but each valid assignment is then
 * applied independently — see the response's `updated` / `failed` split.
 */
export const organizationIntegrationAssignAccountsBatchBodySchema = z.object({
    assignments: z
        .array(
            z.object({
                financialAccountId: z.number(),
                entityId: z.number(),
            }),
        )
        .min(1)
        .max(50),
});

export type OrganizationIntegrationAssignAccountsBatchBody = z.infer<
    typeof organizationIntegrationAssignAccountsBatchBodySchema
>;

export const organizationIntegrationAssignAccountsBatchResponseSchema =
    z.object({
        updated: z.array(
            z.object({
                financialAccountId: z.number(),
                entityId: z.number(),
            }),
        ),
        failed: z.array(
            z.object({
                financialAccountId: z.number(),
                error: z.string(),
            }),
        ),
    });

export type OrganizationIntegrationAssignAccountsBatchResponse = z.infer<
    typeof organizationIntegrationAssignAccountsBatchResponseSchema
>;

/** Upper bound on connections one remind request may target. */
export const REMIND_BATCH_MAX = 50;

/**
 * Asks the people who connected the given disconnected connections to reconnect
 * them. Keyed by *connection*, not account: one login is one recipient, so
 * selecting five accounts under the same login must send one email, not five.
 *
 * Eligibility (disconnected, connected by someone other than the caller, has a
 * reachable recipient, outside the reminder cooldown) is judged per connection
 * and reported in `failed` rather than rejecting the whole request — a single
 * stale row in a bulk selection shouldn't cost the user the other sends.
 */
export const organizationIntegrationRemindConnectionsBodySchema = z.object({
    connectionIds: z.array(z.string()).min(1).max(REMIND_BATCH_MAX),
});

export type OrganizationIntegrationRemindConnectionsBody = z.infer<
    typeof organizationIntegrationRemindConnectionsBodySchema
>;

export const organizationIntegrationRemindConnectionsResponseSchema = z.object({
    sent: z.array(
        z.object({
            connectionId: z.string(),
            recipientEmail: z.string(),
        }),
    ),
    failed: z.array(
        z.object({
            connectionId: z.string(),
            error: z.string(),
        }),
    ),
});

export type OrganizationIntegrationRemindConnectionsResponse = z.infer<
    typeof organizationIntegrationRemindConnectionsResponseSchema
>;
